import { FC, useEffect } from 'react';
import cn from 'classnames';
import ls from 'localstorage-slim';

import { BGHoverComponent } from '../BGHoverComponent/BGHoverComponent';
import { Menu } from '../Menu/Menu';
import { MenuItems } from '../../types/MenuItems';
import { CloseBtn } from '../CloseBtn/CloseBtn';
import { isMenuPageOpen } from '../../helpers/isMenuPageOpen';
import { Icons } from '../Icons/Icons';
import { Sections } from '../../types/Sections';
import { getTranslation } from '../../constants/translations';

import './MenuPageWrapper.scss';

type Props = {
  children: JSX.Element | JSX.Element[];
  id: string;
  color: string;
  title?: string;
  textContent?: string;
};

export const MenuPageWrapper: FC<Props> = ({
  id,
  color,
  title,
  textContent,
  children,
}) => {
  const { hash, pathname } = window.location;
  const section = document.getElementById(id) as HTMLElement;
  const t = getTranslation();

  const goToElement = (id: string) => {
    const element = document.getElementById(id) as HTMLElement;

    element.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      inline: 'nearest',
    });
  };

  useEffect(() => {
    const section = document.getElementById(id) as HTMLElement;
    const firstElement = document.getElementById(
      `${id}-color-part`,
    ) as HTMLElement;

    let prevScrollY = 0;
    let allowScroll = true;
    let isTouchEnd = true;

    if (hash.includes(`${id}`)) {
      section.scrollTo(0, 0);
    }

    window.setTimeout(() => {
      const newSection = document.getElementById(id) as HTMLElement;
      newSection.scrollTo(0, 0);
    }, 700);

    const handleChangeScroll = () => {
      const scrollY = section.scrollTop;

      if (!allowScroll) {
        prevScrollY = scrollY;
        return;
      }

      if (scrollY > prevScrollY) {
        if (section.scrollTop > 30 && isTouchEnd) {
          allowScroll = false;
          goToElement(`${id}-content`);

          document.documentElement.style.overflow = 'hidden';

          window.setTimeout(() => {
            allowScroll = true;
          }, 700);
        }
      } else {
        if (section.scrollTop < firstElement.clientHeight - 30 && isTouchEnd) {
          allowScroll = false;
          goToElement(`${id}-color-part`);

          document.documentElement.style.overflow = 'auto';

          window.setTimeout(() => {
            allowScroll = true;
          }, 700);
        }
      }

      prevScrollY = scrollY;
    };

    if (window.screen.width < 990) {
      section.addEventListener('scroll', handleChangeScroll);
    }

    section.addEventListener('touchstart', () => {
      isTouchEnd = false;
    });
    section.addEventListener('touchend', () => {
      isTouchEnd = true;
    });

    if (!isMenuPageOpen()) {
      window.setTimeout(() => {
        ls.remove('prevMenuPage');
      }, 700);
    }

    if (
      hash.includes(`${id}`) &&
      ls.get('prevMenuPage') &&
      !hash.includes(MenuItems.LOST) &&
      window.screen.width >= 990
    ) {
      section.classList.add('menu-page-wrapper--bg-transparent');

      window.setTimeout(() => {
        section.classList.remove('menu-page-wrapper--bg-transparent');
      }, 690);
    }
  }, [hash]);

  return (
    <section
      className={cn('menu-page-wrapper', {
        'menu-page-animation--page-open-left': hash.includes(`${id}`),
        'menu-page-animation--page-open-right':
          hash.includes(`${id}`) && ls.get('prevMenuPage'),
        'menu-page-animation--page-close':
          !hash.includes(`${id}`) && pathname === '/',
        'menu-page-wrapper__prev-opened':
          isMenuPageOpen() && `${ls.get('prevMenuPage')}`.includes(`${id}`),
        'menu-page-wrapper--outer-page': pathname !== '/',
      })}
      style={{
        animationDelay: `${isMenuPageOpen() && `${ls.get('prevMenuPage')}`.includes(`${id}`) ? '0.7s' : '0s'}`,
      }}
      id={id}
    >
      {pathname === '/' ? (
        <CloseBtn customClassName="menu-page-wrapper__close" />
      ) : (
        <button
          className="menu-page-wrapper__close close-btn"
          onClick={() =>
            window.location.assign(
              window.location.origin + '#' + Sections.EASYBUY,
            )
          }
        >
          {t.COMMON.Home}
        </button>
      )}

      <article
        className="menu-page-wrapper__color-part"
        style={{
          backgroundColor: color,
          height: `${
            window.screen.width < 990
              ? '100vh'
              : section?.scrollHeight + 'px' || '100%'
          }`,
        }}
        id={`${id}-color-part`}
      >
        <BGHoverComponent />

        {!hash.includes(MenuItems.LOST) &&
          !hash.includes(MenuItems.TOGETHER_SALE) &&
          pathname === '/' && <Menu position="absolute" />}

        {Array.isArray(children) ? children[1] : children}

        <button onClick={() => goToElement(`${id}-content`)}>
          <Icons
            name="arrow-circle"
            // customClassName="menu-page-wrapper__nav-btn"
            customClassName={cn(
              'menu-page-wrapper__nav-btn menu-page-wrapper__nav-btn--next',
              {
                'menu-page-wrapper__nav-btn--white': isMenuPageOpen(),
              },
            )}
          />
        </button>
      </article>

      <article className="menu-page-wrapper__content-part" id={`${id}-content`}>
        {Array.isArray(children) ? (
          children[0]
        ) : (
          <div className="menu-page-wrapper__content">
            <h3 className="menu-page-wrapper__title">{title}</h3>

            <p className="menu-page-wrapper__text">{textContent}</p>
          </div>
        )}

        <button
          className="menu-page-wrapper__nav-btn-wrapper"
          onClick={() => goToElement(`${id}-color-part`)}
        >
          <Icons
            name="arrow-circle"
            customClassName={cn(
              'menu-page-wrapper__nav-btn menu-page-wrapper__nav-btn--prev',
            )}
          />
        </button>
      </article>
    </section>
  );
};
